import { Observable, of as observableOf } from 'rxjs';

import { Injectable, OnDestroy } from "@angular/core";
import { TranslateService as Ng2TranslateService } from "@ngx-translate/core";
import { mergeMap } from 'rxjs/operators';
import { BrowserAttributes } from "../../browser/detect.browser";
import { AssetProxy } from "../../http/services/assets.proxy";
import { LogCat, LogManager } from "../../log";
import { TranslateLanguage } from "../interfaces/translate.language";
import {
	ACTION_SUFFIX,
	FLAG_SUFFIX,
	FORM_SUFFIX,
	KENDO_PREFIX,
	LABEL_SUFFIX,
	MESSAGE_SUFFIX,
	PATIENT_SUMMARY_SUFFIX,
	TITLE_SUFFIX,
	WORKLIST_COLUMN_SUFFIX,
	WORKLIST_SUFFIX
} from "../translate.suffix";
import { TranslateProxy } from "./translate.proxy";

@Injectable()
export class TranslateConfigService implements OnDestroy {

	languages: TranslateLanguage[];
	navigatorLanguage: TranslateLanguage;
	forceSpecificLanguage: TranslateLanguage;

	constructor(private translateProxy:TranslateProxy,
				private ng2TranslateService:Ng2TranslateService,
				private assetLoader:AssetProxy,
				private browser:BrowserAttributes) { }

	public ngOnDestroy(): void { }

	/**
	 * The language that currently used.
	 */
	public get currentLang():string {
		return this.ng2TranslateService.currentLang;
	}

	/**
	 * The browser language.
	 */
	public get navigatorLang():string {
		return this.navigatorLanguage.languageKey.toString();
	}

	// get the details of a language by its name including the translation keys in a single call
	public getTranslationAndLang(langCode:string) {
		return this.translateProxy.getTranslationAndLang(langCode);
	}

	/**
	 * Load the data that necessary to the class.
	 * @returns Observable that NOT include the actual data that loaded.
	 */
	public loadDataObservable(useCache:boolean):Observable<any> {
		return this.getLanguages(useCache).pipe(mergeMap((languages:TranslateLanguage[]) => {
			this.languages = languages;

			// Set the default language to be the browser language.
			this.navigatorLanguage = this.getNavigatorLanguage(languages);
			//this.setDefaultLang(this.navigatorLanguage);

			// Set the current language to be the browser language (until the login).
			return this.use(this.navigatorLanguage);
		}));
	}

	/**
	 * Gets an object of translations for a given language.
	 *
	 * @param lang language code.
	 * @returns HttpObservable<Object>
	 */
	/*public getTranslation(lang:string):Observable<Object> {
		return this.translateProxy.getTranslation(lang);
	}*/

	/**
	 * Get an object of the languages in the system.
	 * @returns HttpObservable<Object>
	 */
	public getLanguages(useCache?:boolean):Observable<TranslateLanguage[]> {
		if (useCache && this.languages) {
			return observableOf(this.languages);
		} else {
			return this.translateProxy.getLanguages();
		}
	}

	/**
	 * Get the languages options (just the values without the code).
	 * Necessary: That the function loadData back, other not return a value.
	 *
	 * @returns array with the language options.
	 */
	public getLanguagesOptions():string[] {
		// The data valid after that the function loadData back.
		if (this.languages) {
			return this.languages.map((lang:TranslateLanguage) => lang.language);
		}
	}

	/**
	 * Changes the language currently used.
	 *
	 * @param lang
	 */
	public use(lang:TranslateLanguage):Observable<any> {
		// Load the translation to kendo.ui library.
		// let baseUrl = '//kendo.cdn.telerik.com/2016.2.504/js/messages/kendo.messages.';
		//let scriptUrl='/kendo/js/messages/kendo.messages.'+lang.kendoCode + ".min.js";
		//this.scriptsLoader.getScript('kendo/js/messages/kendo.messages.'+lang.kendoCode + ".min.js");
		/*this.assetLoader.getAsScript(scriptUrl,true).pipe(untilDestroyed(this)).subscribe(retVal=>{

		},()=>{
			// missing build date file -ignore it (this is dev mode)
		});*/
		this.setDefaultLang(lang);
		return this.ng2TranslateService.use(lang.languageKey.toString());
	}

	/**
	 * Sets the default language to use as a fullback.
	 *
	 * @param lang
	 */
	public setDefaultLang(lang:TranslateLanguage):void {
		return this.ng2TranslateService.setDefaultLang(lang.languageKey.toString());
	}

	/**
	 * Gets the translated value of a key (or an array of keys).
	 *
	 * @param key key or keys to translate.
	 * @param interpolateParams parameters.
	 */
	public get(key:string | Array<string>, interpolateParams?:Object):Observable<string | Object> {
		return this.ng2TranslateService.get(key, interpolateParams);
	}

	/**
	 *
	 */
	public resetTranslations() {
		this.ng2TranslateService.translations = {};
	}

	/**
	 * Manually sets an object of translations for a given language
	 * after passing it through the compiler
	 */
	setTranslation(lang: string, translations: Object, shouldMerge?: boolean): void {
		this.ng2TranslateService.setTranslation(lang, translations, shouldMerge);
	}

	/**
	 * Gets the instant translated value of a key (or an array of keys).
	 * /!\ This method is synchronous and the default file loader is asynchronous.
	 * You are responsible for knowing when your translations have been loaded and it is safe to use this method.
	 * If you are not sure then you should use the get method instead.
	 *
	 * @param key key or keys to translate.
	 * @param interpolateParams parameters.
	 */
	public instant(key: string|Array<string>, interpolateParams?: Object): string|Object{
		return this.ng2TranslateService.instant(key, interpolateParams);
	}

	// Return the browser language key that appropriate to the language key in the system.
	public getNavigatorLanguage(languages:TranslateLanguage[]):TranslateLanguage {
		let lang: TranslateLanguage;
		languages = languages.filter(l => l.isActive);

		if (this.browser.isNode()) {
			return this.languages[0];
		} else if (languages.length) {
			// Get the browser language.
			let navigatorLanguage: string = (navigator.language || (<any>navigator).userLanguage).toLowerCase();
			lang = languages.find(l => l.code.toLowerCase() === navigatorLanguage);
			if (lang) {
				return lang;
			}

			// Get the browser language without location.
			lang = languages.find(l => l.code.toLowerCase() === navigatorLanguage.split('-')[0]);
			if (lang) {
				return lang;
			}

			// Get the defined default system language.
			lang = languages.find(l => l.isDefault);
			if (lang) {
				LogManager.debug(LogCat.Translate.Load, "The browser language is not supported. Setting to the configured default language.");
				return lang;
			}

			// Get the defined English language.
			lang = languages.find(l => l.languageKey === 1000);
			if (lang) {
				LogManager.error(LogCat.Translate.Load, "The browser and configured default languages are not supported. Setting to pre-defined English.");
				return lang;
			}

			// Get the first defined active language.
			LogManager.error(LogCat.Translate.Load,
				"The browser, configured default and pre-defined English languages are not supported. Setting to first defined active language.");
			return languages[0];
		}

		// Nothing is defined as active, default or English. Get the first defined language.
		LogManager.error(LogCat.Translate.Load,
			"The browser, configured default and pre-defined English languages are not supported or not active. Setting to first defined language.");
		return this.languages[0];
	}

	// Checks if title for translate already contains registered translate suffix
	public containsRegisteredTranslateSuffix(title: string): boolean {
		let suffixes: string[] = [ACTION_SUFFIX, FLAG_SUFFIX, FORM_SUFFIX, LABEL_SUFFIX, MESSAGE_SUFFIX, PATIENT_SUMMARY_SUFFIX,
			WORKLIST_COLUMN_SUFFIX, WORKLIST_SUFFIX, TITLE_SUFFIX, KENDO_PREFIX];
		return !suffixes.every(suffix => title ? title.indexOf(suffix) < 0 : false);
	}
}
